import "./ActualVsPredicted.scss";
import React, { useEffect, useRef, useState } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ChartTooltip from "../../Components/Charts/ChartTooltip/ChartTooltip";
import AutoSizer from "react-virtualized-auto-sizer";
import * as R from "ramda";
import { ActualVsPredictedData } from "./ModelOverview";
import { CARTESIAN_GRID_STROKE_WIDTH, TICK_STYLE } from "../../Components/Charts/ChartConstants";
import { headerFormatter, useDynamicYAxisWidth } from "../../Components/Charts/ChartUtils";
import { useCompanyInfo } from "../../redux/company";
import { dateToMddyy } from "../../utils/date-utils";
import { Brand20, Brand60, Neutral400, Neutral500 } from "../../utils/colors";

interface ActualVsPredictedProps {
  data: ActualVsPredictedData[];
  dateGrouping: string;
  legendValue: string;
  syncId?: number;
  tooltipFormatter?: (value: any) => string;
  yAxisFormatter?: (value: any) => string;
  yAxisStaticWidth?: number;
  setChartYAxisDynamicWidth?: (number) => void;
  height?: string;
  width?: string;
}

const RIGHT_MARGIN = 30;
const TOP_MARGIN = 20;

const ActualVsPredicted: React.FC<ActualVsPredictedProps> = ({
  data,
  dateGrouping,
  legendValue,
  syncId,
  tooltipFormatter,
  yAxisFormatter,
  yAxisStaticWidth,
  setChartYAxisDynamicWidth,
  height = "100%",
  width = "100%",
}) => {
  const trainEnd = data[data.findIndex(d => d.train_test === "test") - 1];
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  const [trainAreaWidth, setTrainAreaWidth] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const { yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    data: data,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (containerRef.current && trainAreaWidth === 0) {
        const trainArea: SVGPathElement | null = containerRef.current.querySelector(
          ".recharts-wrapper .recharts-surface .recharts-reference-area path"
        );

        if (trainArea) {
          setTrainAreaWidth(Number(trainArea?.getAttribute("width")) ?? 0);
        }
      }
    }, 500);

    if (trainAreaWidth > 0) {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [containerRef, trainAreaWidth]);

  useEffect(() => {
    if (setChartYAxisDynamicWidth) {
      setChartYAxisDynamicWidth(yAxisWidth);
    }
  }, [setChartYAxisDynamicWidth, yAxisWidth]);

  return (
    <div className="actualVsPredictedWrapper">
      <div className="actualVsPredictedGraph" style={{ height, width }} ref={containerRef}>
        <div
          className="actVPredTitle"
          style={{ marginLeft: yAxisStaticWidth || yAxisWidth, marginRight: RIGHT_MARGIN }}
        >
          <div
            className="periodLegend trainPeriod"
            style={{ width: trainAreaWidth ? trainAreaWidth - 32 : "100%" }}
          >
            Train Period
            <div style={{ backgroundColor: Brand20 }} className="square" />
          </div>
          <div className="periodLegend testPeriod">Test Period</div>
        </div>
        <AutoSizer>
          {({ width, height }) => (
            <ComposedChart
              data={data}
              height={height}
              margin={{ top: TOP_MARGIN, right: RIGHT_MARGIN, left: 0, bottom: 0 }}
              syncId={syncId}
              ref={setChartRef}
              width={width}
            >
              <XAxis dataKey="date" tick={TICK_STYLE} tickFormatter={val => dateToMddyy(val)} />
              <YAxis
                tick={TICK_STYLE}
                tickFormatter={yAxisFormatter}
                tickLine={false}
                axisLine={false}
                width={yAxisStaticWidth || yAxisWidth}
              />
              <ReferenceArea fill={Brand20} x2={trainEnd?.date} />
              <Area
                dataKey="actual_kpi"
                fill={Neutral400}
                isAnimationActive={false}
                stroke={Neutral400}
                fillOpacity={1}
              />
              <Line
                dataKey="predicted_kpi"
                dot={false}
                isAnimationActive={false}
                stroke={Brand60}
                strokeWidth={3}
                type="monotone"
              />
              <ReferenceLine stroke={Neutral500} x={trainEnd?.date} />
              <CartesianGrid
                stroke={Neutral500}
                strokeOpacity={0.25}
                strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                vertical={false}
              />
              <Tooltip
                content={({ label, payload }) => {
                  if (!payload || !label) {
                    return null;
                  }
                  const formattedLabel = headerFormatter(dateGrouping, label, fiscalCal);
                  let items = R.sortBy(
                    (item: any) => -Math.abs(item.value),
                    payload?.map(item => ({
                      label: (item?.name ?? "")
                        .toString()
                        .split("_")
                        .map(item => item.charAt(0).toUpperCase() + item.slice(1))
                        .join(" ")
                        .replace("Kpi", "KPI") as string,
                      value: tooltipFormatter
                        ? tooltipFormatter(item.value)
                        : (item.value as number),
                      color: item.stroke,
                    })) || []
                  );

                  return <ChartTooltip headerLabel={formattedLabel} items={items} />;
                }}
                cursor={{ stroke: Neutral500, width: "1px" }}
                isAnimationActive={false}
              />
            </ComposedChart>
          )}
        </AutoSizer>
      </div>
      <div className="rightOfChart">
        <div className="legend">
          <div className="legendItem">
            <div className="square" style={{ backgroundColor: Neutral400 }} />
            <div>{`Actual ${legendValue}`}</div>
          </div>
          <div className="legendItem">
            <div className="line" />
            <div>{`Predicted ${legendValue}`}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActualVsPredicted;
